import { useEffect } from 'react';
import type { MutableRefObject } from 'react';

/**
 * Mobile browsers (iOS Safari, Android Chrome) freeze timers and silently kill
 * WebSockets when a tab is backgrounded or the phone is locked. When the user
 * returns, `readyState` can still read OPEN even though the socket is a zombie
 * that will never deliver another message.
 *
 * This watchdog:
 *  - pings the socket on a short interval and treats it as dead if no message
 *    has arrived for a while (zombie detection);
 *  - re-checks immediately whenever the page becomes visible / focused / online
 *    again (phone unlock, tab switch, network change), so a dropped connection
 *    is repaired within a few seconds instead of hanging indefinitely.
 */

const CHECK_INTERVAL_MS = 3000;
const PING_INTERVAL_MS = 10000;
const STALE_MS = 20000;

export interface ConnectionWatchdogOptions {
  wsRef: MutableRefObject<WebSocket | null>;
  /** Guard ref that is true once the consumer intentionally tore the socket down. */
  stoppedRef: MutableRefObject<boolean>;
  /** Timestamp (ms) of the last inbound message; kept fresh by the socket hook. */
  lastMessageAtRef: MutableRefObject<number>;
  /** Force a fresh connection with the same parameters. */
  reconnect: () => void;
  /** Serialized keep-alive frame (defaults to a plain ping). */
  pingMessage?: string;
}

export function useConnectionWatchdog({
  wsRef,
  stoppedRef,
  lastMessageAtRef,
  reconnect,
  pingMessage = JSON.stringify({ type: 'ping' }),
}: ConnectionWatchdogOptions): void {
  useEffect(() => {
    let lastPingAt = 0;
    let wasHidden = document.visibilityState === 'hidden';
    let lastWakeReconnectAt = 0;

    const check = () => {
      if (stoppedRef.current) return;
      const ws = wsRef.current;

      // No socket, or one that is closing/closed: rebuild it now.
      if (!ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
        reconnect();
        return;
      }

      const now = Date.now();

      // Handshaking (CONNECTING) or open, but nothing has happened for a long
      // time — a socket frozen mid-connect on sleep, or an open zombie. Rebuild.
      if (now - lastMessageAtRef.current > STALE_MS) {
        reconnect();
        return;
      }

      // Still handshaking and not yet stale — give it time.
      if (ws.readyState !== WebSocket.OPEN) return;

      if (now - lastPingAt >= PING_INTERVAL_MS) {
        lastPingAt = now;
        try {
          ws.send(pingMessage);
        } catch {
          reconnect();
        }
      }
    };

    const reconnectOnWake = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      // A visible/pageshow/online sequence may emit several events together.
      if (now - lastWakeReconnectAt < 500) return;
      lastWakeReconnectAt = now;
      reconnect();
    };

    const onVisibilityChange = () => {
      const isHidden = document.visibilityState === 'hidden';
      const becameVisible = wasHidden && !isHidden;
      wasHidden = isHidden;
      if (becameVisible) reconnectOnWake();
    };

    const onPageShow = (event: PageTransitionEvent) => {
      // Restoring from the back-forward cache can retain a visually OPEN but
      // detached WebSocket. A normal first pageshow only needs the usual check.
      if (event.persisted) reconnectOnWake();
      else check();
    };

    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', reconnectOnWake);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', reconnectOnWake);
    };
  }, [wsRef, stoppedRef, lastMessageAtRef, reconnect, pingMessage]);
}
